import { CommonModule } from '@angular/common';
import { Component, inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FlexLayoutModule } from '@angular/flex-layout';
import { FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import {
  MAT_DIALOG_DATA,
  MatDialogModule,
  MatDialogRef,
} from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { MatTabsModule } from '@angular/material/tabs';
import { MatTooltipModule } from '@angular/material/tooltip';
import { finalize, Subject } from 'rxjs';
import {
  CashRegister,
  GetBusinessDirectionByIdQuery,
  GetEconomicNatureByIdQuery,
} from 'src/app/graphql/types';
import { LoadingComponent } from 'src/app/main/pages/generals/loading/loading.component';
import { BusinessDirectionService } from 'src/app/main/pages/services/business-direction.service';
import { CashRegisterService } from 'src/app/main/pages/services/cash-register.service';
import { EconomicNatureService } from 'src/app/main/pages/services/economic-nature.service';
import { PointEmissionJson } from 'src/app/main/pages/services/microservices/json/point-emission.json';
import { WarehouseService } from 'src/app/main/pages/services/warehouse.service';
import { NumerosUtils } from 'src/app/shared';

import { BaseDialogComponent } from '../../../../../generals/dialog/base-dialog/base-dialog.component';
import { BusinessUnitService } from '../../../../../services/business-unit.service';
import { BusinessUnitDirectionSimpleJson } from '../../../../../services/microservices/json/business-unit-direction-simple.json';
import { BusinessUnitDirectionJson } from '../../../../../services/microservices/json/business-unit-direction.json';
import { BusinessSteps } from '../../data/businessSteps.enum';
import { BusinessDirectionComponent } from '../../forms/business-direction/business-direction.component';
import { EconomicNatureComponent } from '../../forms/economic-nature/economic-nature.component';
import { PointEmissionComponent } from '../../forms/point-emission/point-emission.component';
import { WarehouseComponent } from '../../forms/warehouse/warehouse.component';
import { CashRegisterDialogComponent } from '../cash-register-dialog/cash-register-dialog.component';

@Component({
  selector: 'app-business-unit-dialog',
  templateUrl: './business-unit-dialog.component.html',
  styleUrls: ['./business-unit-dialog.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    FlexLayoutModule,
    MatInputModule,
    MatButtonModule,
    MatDialogModule,
    MatTooltipModule,
    MatSelectModule,
    MatIconModule,
    MatTableModule,
    MatTabsModule,
    BusinessDirectionComponent,
    EconomicNatureComponent,
    PointEmissionComponent,
    WarehouseComponent,
    BaseDialogComponent,
    LoadingComponent,
  ],
})
export class BusinessUnitDialogComponent implements OnInit, OnDestroy {
  readonly dialog: MatDialogRef<
    BusinessUnitDialogComponent,
    BusinessUnitDirectionSimpleJson
  > = inject(MatDialogRef);

  private businessDirectionService = inject(BusinessDirectionService);
  private businessUnitService = inject(BusinessUnitService);
  private economicNatureService = inject(EconomicNatureService);
  private cashRegisterService = inject(CashRegisterService);
  private warehouseService = inject(WarehouseService);
  private _snackBar = inject(MatSnackBar);
  private _unsubscribeAll!: Subject<any>;

  public idsBusiness: { businessUnitId: number; branchOfficeId: number } =
    inject(MAT_DIALOG_DATA);

  @ViewChild(BusinessDirectionComponent)
  businessDirectionRef!: BusinessDirectionComponent;
  @ViewChild(EconomicNatureComponent)
  economicNatureRef!: EconomicNatureComponent;
  @ViewChild(PointEmissionComponent)
  pointEmissionRef!: PointEmissionComponent;
  @ViewChild(CashRegisterDialogComponent)
  cashRegisterDialog!: CashRegisterDialogComponent;
  @ViewChild(WarehouseComponent)
  warehouseRef!: WarehouseComponent;

  businessDirectionJson!: BusinessUnitDirectionJson;
  tabSelected: number = BusinessSteps.BUSINESS_DIRECTION;
  isLoading = false;
  txtBtn = 'SIGUIENTE';
  isActiveBtn = false;
  businessUnitForm!: FormGroup;
  economicNatureForm!: FormGroup;
  pointEmissionForm!: FormGroup;
  formDataBusiness!: GetBusinessDirectionByIdQuery & { id: number };
  formDataEconomic!: GetEconomicNatureByIdQuery & { id: number };
  formDataPoint!: MatTableDataSource<PointEmissionJson>;
  isEditing = false;
  isValidForm = false;
  isEnabledEconomic = false;
  isEnabledPoint = false;
  isEnabledWarehouse = false;
  isUpdateWarehouse = false;

  ngOnInit(): void {
    this._unsubscribeAll = new Subject<any>();
    this.initialLoad();
  }

  initialLoad() {
    this.getBusinessDirection();
  }

  validarTab(tab: any) {
    this.tabSelected = tab.index;
    switch (tab.index) {
      case BusinessSteps.BUSINESS_DIRECTION:
        this.businessDirectionRef.formValidate();
        this.businessDirectionRef.startFormValidator();
        break;
      case BusinessSteps.ECONOMIC_NATURE:
        this.economicNatureRef.formValidate();
        this.economicNatureRef.startFormValidator();
        break;
      case BusinessSteps.POINT_EMISSION:
        this.pointEmissionRef.loadPointEmission();
        this.txtBtn = 'SIGUIENTE';
        break;
      case BusinessSteps.WAREHOUSE:
        this.warehouseRef.loadWareHouse();
        this.txtBtn = 'FINALIZAR';
        break;
    }
  }

  businessFormValid(formValid: { status: boolean; form: FormGroup }) {
    if (!formValid.status) {
      return;
    }
    this.isActiveBtn = formValid.status;
    this.businessUnitForm = formValid.form;
    this.txtBtn = this.isEnabledEconomic ? 'ACTUALIZAR' : 'SIGUIENTE';
  }

  economicFormValid(formValid: { status: boolean; form: FormGroup }) {
    if (!formValid) {
      return;
    }
    this.isActiveBtn = formValid.status;
    this.economicNatureForm = formValid.form;
    if (!this.economicNatureForm.get('id')?.value) {
      this.economicNatureForm.patchValue({
        id: this.idsBusiness.businessUnitId,
      });
    }
    this.txtBtn = this.isEnabledPoint ? 'ACTUALIZAR' : 'SIGUIENTE';
  }

  getBusinessDirection() {
    if (this.idsBusiness.businessUnitId < 1) {
      return;
    }
    this.businessDirectionService
      .get(this.idsBusiness.businessUnitId)
      .pipe(finalize(() => (this.isLoading = false)))
      .subscribe({
        next: response => {
          if (response.data.getBusinessDirectionById) {
            this.formDataBusiness = this.businessFormAsigned(response.data);
            this.isEnabledEconomic = true;
            this.isActiveBtn = true;
            this.txtBtn = 'ACTUALIZAR';
            this.getEconomicNature();
          } else {
            this.businessUnitForm.patchValue({
              id: this.idsBusiness.businessUnitId,
            });
            this.isEnabledEconomic = false;
            this.txtBtn = 'ACTUALIZAR';
          }
        },
      });
  }

  getEconomicNature() {
    this.economicNatureService
      .get(this.idsBusiness.businessUnitId)
      .pipe(
        finalize(() => {
          this.isLoading = false;
        }),
      )
      .subscribe({
        next: ({ data }) => {
          if (data?.getEconomicNatureById) {
            this.formDataEconomic = this.economicFormAsigned(data);
            this.isEnabledPoint = true;
            this.checkWarehouseRecords();
            return;
          }
          this.isEnabledPoint = false;
        },
        error: err => {
          this.isEnabledPoint = false;
          console.error(`Error al obtener la naturaleza economica: ${err}`);
        },
      });
  }

  checkWarehouseRecords() {
    this.warehouseService
      .getRecords(this.idsBusiness.businessUnitId)
      .subscribe(records => {
        if (records && records.length > 0) {
          this.isEnabledWarehouse = true;
        }
      });
  }

  businessFormAsigned(json: GetBusinessDirectionByIdQuery) {
    return {
      id: this.idsBusiness.businessUnitId,
      province: Number(json.getBusinessDirectionById?.canton?.provinceId),
      canton: Number(json.getBusinessDirectionById?.canton?.id),
      street: json.getBusinessDirectionById?.street,
      streetNumber: json.getBusinessDirectionById?.streetNumber,
      intersection: json.getBusinessDirectionById?.intersection,
      storeName: json.getBusinessDirectionById?.storeName,
    };
  }

  economicFormAsigned(json: GetEconomicNatureByIdQuery) {
    return {
      id: this.idsBusiness.businessUnitId,
      architecture: Number(json.getEconomicNatureById?.architecture?.id),
      prototype: Number(json.getEconomicNatureById?.prototype?.id),
      sector: Number(json.getEconomicNatureById?.sector?.id),
      bancking: 1000,
    };
  }

  private openSnack(message: string): void {
    this._snackBar.open(message, '', {
      verticalPosition: 'top',
      horizontalPosition: 'right',
      panelClass: ['snack-error'],
      duration: 5000,
    });
  }

  submit(): void {
    this.isLoading = true;
    switch (this.tabSelected) {
      case BusinessSteps.BUSINESS_DIRECTION:
        if (this.idsBusiness.businessUnitId > 0) {
          this.updateBusinessDirection();
        } else {
          this.saveBusinessDirection();
        }
        break;
      case BusinessSteps.ECONOMIC_NATURE:
        this.saveEconomicNature();
        break;
      case BusinessSteps.POINT_EMISSION:
        this.isLoading = false;
        this.txtBtn = 'FINALIZAR';
        this.isEnabledWarehouse = true;
        this.warehouseRef.loadWareHouse();
        this.changeTab(BusinessSteps.WAREHOUSE);
        break;
      case BusinessSteps.WAREHOUSE:
        this.isLoading = false;
        this.dialog.close();
        break;
    }
  }

  private saveBusinessDirection(): void {
    this.businessUnitService
      .save(this.businessUnitForm)
      .subscribe(({ data, errors }) => {
        if (data?.saveBusinessUnit?.id) {
          this.businessUnitForm.patchValue({ id: data.saveBusinessUnit?.id });
          this.idsBusiness.businessUnitId = Number(data.saveBusinessUnit?.id);
          this.isLoading = false;
          if (this.txtBtn === 'SIGUIENTE') {
            this.changeTab(BusinessSteps.ECONOMIC_NATURE);
            this.isEnabledEconomic = true;
          }
        }
        if (errors) {
          this.isLoading = false;
        }
      });
  }

  private saveEconomicNature() {
    this.economicNatureService
      .save(this.economicNatureForm)
      .subscribe(({ data, errors }) => {
        if (data) {
          if (this.txtBtn === 'SIGUIENTE') {
            this.changeTab(BusinessSteps.POINT_EMISSION);
            this.pointEmissionRef.loadPointEmission();
            this.isEnabledPoint = true;
          }
          this.isLoading = false;
        }
        if (errors) {
          this.isLoading = false;
        }
      });
  }

  private updateBusinessDirection(): void {
    const businessUnitId = this.idsBusiness.businessUnitId;
    if (businessUnitId > 0) {
      this.businessUnitForm.patchValue({ id: businessUnitId });
    }
    this.businessUnitService
      .update(this.businessUnitForm)
      .subscribe(({ data, errors }) => {
        if (data) {
          this.isLoading = false;
          if (this.txtBtn === 'SIGUIENTE') {
            this.changeTab(BusinessSteps.ECONOMIC_NATURE);
            this.isEnabledEconomic = true;
          }
        }
        if (errors) {
          this.isLoading = false;
        }
      });
  }

  changeTab(tab: number) {
    this.tabSelected = tab;
  }

  private objectToArray(objects: any): Array<PointEmissionJson> {
    const array: [] = JSON.parse(JSON.stringify(objects));
    const json: Array<PointEmissionJson> = [];
    array.forEach(object => {
      json.push(this.objectToJson(object));
    });
    return json!;
  }

  private objectToJson(object: CashRegister): PointEmissionJson {
    const number = NumerosUtils.addZero(Number(object.number));
    const json: PointEmissionJson = {
      id: object.id?.toString()!,
      name: object.name?.toString()!,
      number,
      user: object.user?.name!,
      active: object.active!,
      creationDate: object.creationDate?.toString()!,
    };
    return json;
  }

  ngOnDestroy(): void {
    this._unsubscribeAll.next(void 0);
    this._unsubscribeAll.complete();
  }
}
